import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, Typography, FormControlLabel, Checkbox, TextField, Card, Autocomplete, Icon, IconButton, Tooltip, Modal } from '@mui/material';
import { CommonPatientCard, CommonPatientDetails, ConsultantPatientCard, ToolsFieldWithMic } from '../../../../../Components/Common Components/CommonComponents';
import { withTranslation } from 'react-i18next';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import { getCachevalue, localGetItem } from '../../../../../Utility/Services/CacheProviderService';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg'
import Loader from '../../../../../Components/Loader';
import { CheckAccessFunc } from '../../../../../Components/CommonFunctions/CommonFunctions';
import { ImagePaths } from '../../../../../Utility/ImagePaths';
import { DataGrid } from '@mui/x-data-grid';

const formatLabel = (value) => {
    return value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

const reverseFormatLabel = (label) => {
    return label.toLowerCase().replace(/\s+/g, '_');
};

// post convert data
const getTrueArray = (systemData) => {
    return Object.entries(systemData.selection || {}).reduce((acc, [key, val]) => {
        if (val) acc.push(formatLabel(key));
        return acc;
    }, []);
};

const getTrueValueObj = (assessment) => {
    const result = {};

    Object.entries(assessment).forEach(([system, symptoms]) => {
        if (system === "remarks") return;

        const trueKeys = Object.entries(symptoms).reduce((acc, [key, value]) => {
            if (value) acc.push(formatLabel(key));
            return acc;
        }, []);

        if (trueKeys.length > 0) {
            result[formatLabel(system)] = trueKeys;
        }
    });

    return result;
};

// get convert data
const updateSelectionFromLabels = (trueLabels, systemObject) => {
    const labelToKey = (label) => reverseFormatLabel(label);

    const trueKeys = new Set(trueLabels.map(labelToKey));

    const updatedSelection = Object.fromEntries(
        Object.keys(systemObject.selection).map(key => [
            key,
            trueKeys.has(key)
        ])
    );

    return {
        ...systemObject,
        selection: updatedSelection
    };
};

const mapSummaryToAssessment = (summary, template) => {
    const updated = JSON.parse(JSON.stringify(template));
    if (summary) {
        Object.entries(summary).forEach(([systemKey, trueLabels]) => {
            const system = reverseFormatLabel(systemKey);
            if (!updated[system]) return;

            Object.entries(updated[system]).forEach(([symptomKey]) => {
                const formatted = formatLabel(symptomKey);
                updated[system][symptomKey] = trueLabels.includes(formatted);
            });
        });
    }

    return updated;
};

const defaultState = {
    system_wise_assesment: {
        eye: {
            imparied_vision: false,
            use_spectacles: false,
            icterus_pain: false,
            redness: false,
            discharge: false,
        },
        ear: {
            imparied_hearing: false,
            discharge: false,
            deaf: false,
            wax: false,
        },
        scalp: {
            clean: false,
            pendiculi: false,
            dandruff: false,
        },
        nose: {
            clean: false,
            discharge: false,
            epistaxis: false,
        },
        remarks: ""
    },
    respiratory_system: {
        selection: {
            cough: false,
            sputum: false,
            bradypnoea: false,
            dyspnoea: false,
            orthopnoea: false,
            tachypnoea: false,
        },
        remarks: ""
    },
    cardio_vascular_system: {
        selection: {
            fatigue: false,
            palpitation: false,
            syncope: false,
            chest_pain: false,
            irregular_heart_rate: false,
            oedema: false,
            pacemaker_temp: false,
            pacemaker_permanent: false,
            neck_vein_distension: false,
        },
        remarks: ""
    },
    gastro_intentional_system: {
        selection: {
            dentures: false,
            loose_teeth: false,
            tongue_coated: false,
            oral_ulcers: false,
            abdominal_distension: false,
            nausea: false,
            vomiting: false,
            oral_feeding: false,
            tube_feeding: false,
            bowel_opened: false,
            constipation: false,
            diarrhoea: false,
            malena: false,
        },
        remarks: ""
    },
    genito_urinary_system: {
        selection: {
            haematuria: false,
            retention: false,
            frequency: false,
            pain: false,
            catheter: false,
        },
        remarks: ""
    },
    central_nervous_system: {
        selection: {
            alert: false,
            drowsy: false,
            confused: false,
            consiousness: false,
            unconsious: false,
            paraplegia: false,
            hemiplegia: false,
            quadriplegia: false,
            speech_relevant: false,
            speech_slurred: false,
            speech_irrelevant: false,
            aphasic: false,
        },
        remarks: ""
    },
    musculo_skeletal_system: {
        selection: {
            weakness: false,
            tremars: false,
            fracture: false,
            muscle_pain: false,
            deformity: false,
            joint_pain: false
        },
        remarks: ""
    },
    emotional_status: {
        selection: {
            anxious: false,
            calm: false,
            angry: false,
            cooperative: false,
            fearful: false,
            restless: false,
            withdrawn: false,
        },
        remarks: ""
    },
    skin_integrity: {
        selection: {
            intact: false,
            breakdown: false,
            rash: false,
            blister: false,
            infection: false,
            icterus: false,
            odema: false,
        },
        remarks: ""
    },
    falls_risk: {
        selection: {
            age_below_13: false,
            age_below_70: false,
            physical_disability: false,
            semiconicious: false,
            restless: false,
            mucle_weakness: false,
            disoriented: false,
        },
        remarks: ""
    },
    eating_habits: {
        diet: {
            vegetarian: false,
            non_veg: false,
        },
        eating: {
            independent: false,
            dependent: false,
            needs_assistance: false,
            ryles_tube: false,
        },
        hygiene: {
            independent: false,
            dependent: false,
            needs_assistance: false,
        },
        addiction: {
            tobacco: false,
            alcohol: false,
            opium: false
        },
        sleep_adequate: {
            disturbed: false,
        },
        remarks: ""
    },
}

const NurseAssessments = (props) => {
    const [state, setState] = useState({
        // patient details
        attender_name: "",
        attender_mobile: "",
        doctor_name: "",
        doctor_id: "",
        patientData: {},
        withOutAppointment: {},
        clinic_id: JSON.parse(localGetItem('SelectedDoctorClinicId')),
        // state value
        NurseList: [],
        nurseName: null,
        ...JSON.parse(JSON.stringify(defaultState)),
        o2_remarks: "",
        notes: "",
        // message
        isErrorMsg: false,
        isErrorMsgText: '',
        successMsg: false,
        successMsgText: '',
        isLoader: false,
        isHistory: false,
        historyData: [],
    })

    useEffect(() => {
        const patientDatas = JSON.parse(getCachevalue("IppatientData") || "{}");
        const appointmentDetail = JSON.parse(getCachevalue("withoutAppointment") || "{}");
        const samePatient = appointmentDetail?.patient_id === patientDatas?.patient_id;
        getNurseList();
        setState((prevState) => ({
            ...prevState,
            patientData: patientDatas,
            withOutAppointment: samePatient ? appointmentDetail : undefined
        }));
    }, [])

    useEffect(() => {
        if (state?.patientData?.patient_id || state?.withOutAppointment?.patient_id) {
            getPatientData();
            getNurseAssessmentData();
        }
    }, [state?.patientData?.patient_id, state?.withOutAppointment?.patient_id])

    const errorMessage = useCallback((message) => {
        setState((prevState) => ({
            ...prevState,
            isErrorMsg: true,
            isErrorMsgText: message
        }))
    }, [state])

    const successMessage = useCallback((message) => {
        setState((prevState) => ({
            ...prevState,
            successMsg: true,
            successMsgText: message
        }))
    }, [state])

    const msgClose = useCallback(() => {
        setState((prevState) => ({
            ...prevState,
            isErrorMsg: false,
            isErrorMsgText: '',
            successMsg: false,
            successMsgText: ''
        }))
    }, [state])

    const LoaderFunction = useCallback((key = false) => {
        setState((prevState) => ({
            ...prevState,
            isLoader: key
        }))
    }, [state])

    const getNurseAssessmentData = useCallback(() => {
        const admission_id = state.patientData?.id || state.withOutAppointment?.ip_admission_id;
        let url = `${(Serviceurls.IP_DOCNOTES_NURSE_ASSESSMENTS)}?ip_admission_id=${admission_id}`;
        if (props.is_From_IpNotes && state.clinic_id) {
            url = `${url}&clinic_id=${state.clinic_id}`
        }
        try {
            RestAPIService.getAll(url)
                .then((response) => {
                    if (response.data.status === 'success') {
                        const Data = response.data.data || {};
                        if (Object.keys(Data)?.length > 0) {
                            setState((prevState) => ({
                                ...prevState,
                                // nurseName: Data?.nurse_profile_id ? state.NurseList?.find(item => item?.staff_id === Data?.nurse_profile_id) : null,
                                system_wise_assesment: {
                                    ...mapSummaryToAssessment(Data?.system_wise_assesment, defaultState.system_wise_assesment),
                                    remarks: Data?.system_wise_assesment_remarks || ""
                                },
                                respiratory_system: {
                                    ...updateSelectionFromLabels(Data?.respiratory_system, defaultState.respiratory_system),
                                    remarks: Data?.respiratory_system_remarks || ""
                                },
                                o2_remarks: Data?.o2_remarks || "",
                                cardio_vascular_system: {
                                    ...updateSelectionFromLabels(Data?.cardio_vascular_system, defaultState.cardio_vascular_system),
                                    remarks: Data?.cardio_vascular_system_remarks || ""
                                },
                                gastro_intentional_system: {
                                    ...updateSelectionFromLabels(Data?.gastro_intentional_system, defaultState.gastro_intentional_system),
                                    remarks: Data?.gastro_intentional_system_remarks || ""
                                },
                                genito_urinary_system: {
                                    ...updateSelectionFromLabels(Data?.genito_urinary_system, defaultState.genito_urinary_system),
                                    remarks: Data?.genito_urinary_system_remarks || ""
                                },
                                central_nervous_system: {
                                    ...updateSelectionFromLabels(Data?.central_nervious_system, defaultState.central_nervous_system),
                                    remarks: Data?.central_nervious_system_remarks || ""
                                },
                                musculo_skeletal_system: {
                                    ...updateSelectionFromLabels(Data?.musculo_skeletal_system, defaultState.musculo_skeletal_system),
                                    remarks: Data?.musculo_skeletal_system_remarks || ""
                                },
                                emotional_status: {
                                    ...updateSelectionFromLabels(Data?.emotional_status, defaultState.emotional_status),
                                    remarks: Data?.emotional_status_remarks || ""
                                },
                                skin_integrity: {
                                    ...updateSelectionFromLabels(Data?.skin_integrity, defaultState.skin_integrity),
                                    remarks: Data?.skin_integrity_remarks || ""
                                },
                                falls_risk: {
                                    ...updateSelectionFromLabels(Data?.fall_risk, defaultState.falls_risk),
                                    remarks: Data?.fall_risk_remarks || ""
                                },
                                eating_habits: {
                                    ...mapSummaryToAssessment(Data?.eating_habits, defaultState.eating_habits),
                                    remarks: Data?.eating_habits_remarks || ""
                                },
                                notes: Data?.notes || "",
                            }))
                        }
                        setTimeout(() => {
                            getHistoryData()
                        }, 1000);
                    }
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }, [state.patientData?.id, state.withOutAppointment?.ip_admission_id, props.is_From_IpNotes, state.clinic_id, state.NurseList])

    const getPatientData = useCallback(() => {
        let admission_id = state.patientData?.id || state.withOutAppointment?.ip_admission_id;
        let url = `${(localGetItem('isNewBilling') === "false" ? Serviceurls.FO_PATIENT_DETAILS : Serviceurls.FO_PATIENT_DETAILS_NEW)}?admission_id=${admission_id}`;
        if (props.is_From_IpNotes && state.clinic_id) {
            url = `${url}&clinic_id=${state.clinic_id}`
        }
        try {
            RestAPIService.getAll(url)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setState((prevState) => ({
                            ...prevState,
                            attender_name: response.data.data[0].attender_name,
                            attender_mobile: response.data.data[0].attender_mobile,
                            doctor_name: response.data.data[0].doctor_name,
                            doctor_id: response.data.data[0].doctor_id,
                        }))
                    }
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }, [state.patientData?.id, state.withOutAppointment?.ip_admission_id, state.clinic_id])

    const getNurseList = useCallback(() => {
        try {
            RestAPIService.getAll(Serviceurls.FO_STAFF_LIST + `?clinic_id=${state.clinic_id}`)
                .then((response) => {
                    if (response.data.status === "success") {
                        let nurseData = null
                        if (response.data.data && localGetItem("NurseId") && !state?.nurseName) {
                            let nurseId = JSON.parse(localGetItem("NurseId"))
                            nurseData = response.data.data.find((item) => item.staff_id === nurseId)
                        }
                        setState((prevState) => ({
                            ...prevState,
                            NurseList: response.data.data,
                            nurseName: nurseData
                        }))
                    }
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }, [state.clinic_id])

    const getHistoryData = useCallback(() => {
        try {
            const admission_id = state.patientData?.id || state.withOutAppointment?.ip_admission_id;
            RestAPIService.getAll(Serviceurls.NURSE_ASSESMENT_LOG_GET + `?ip_admission_id=${admission_id}&clinic_id=${state.clinic_id}`)
                .then((response) => {
                    if (response.data.status === "success") {
                        setState((prevState) => ({
                            ...prevState,
                            historyData: response.data.data
                        }))
                    }
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }, [state.patientData?.id, state.withOutAppointment?.ip_admission_id, state.clinic_id])

    const renderCard = useCallback((stateKey) => {
        return (
            <Card sx={{ padding: "0.625dvw", marginBottom: "0.5vw" }}>
                <Typography fontSize={"1vw"} padding={"0.5dvw"} fontWeight={"bold"}
                    color={"gray"} borderBottom={"0.5px solid gray"} marginBottom={"0.5vw"}>
                    {stateKey === "system_wise_assesment" ? "System Wise Assessment" : formatLabel(stateKey)}
                </Typography>
                {Object.keys(state[stateKey]).map((system) => {
                    return (
                        <Box sx={{ mb: "0.3dvw" }}>
                            {system !== "remarks" && <Box display={"flex"} alignItems={"center"} gap={"0.625vw"}>
                                {system !== "selection" && <Typography fontSize={"1vw"} flex={0.1}>{formatLabel(system)}</Typography>}
                                <Box className="eMed_CheckBox_Group" flex={system !== "selection" ? 0.9 : 1}>
                                    {Object.entries(state[stateKey][system]).map(([key, value]) => (
                                        <FormControlLabel
                                            key={key}
                                            sx={{ width: "15dvw", marginBottom: "0.5dvw" }}
                                            control={
                                                <Checkbox
                                                    sx={{ fontSize: '0.4vw' }}
                                                    size="small"
                                                    checked={value}
                                                    onChange={(e) => {
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            [stateKey]: {
                                                                ...prevState[stateKey],
                                                                [system]: {
                                                                    ...prevState[stateKey][system],
                                                                    [key]: e.target.checked,
                                                                }
                                                            }
                                                        }))
                                                    }}
                                                />
                                            }
                                            label={formatLabel(key)}
                                        />
                                    ))}
                                </Box>
                            </Box>}

                            {system === "remarks" && <TextField
                                label="Remarks"
                                fullWidth
                                multiline
                                sx={{ mt: 2 }}
                                value={state[stateKey].remarks}
                                inputProps={{ maxLength: 250 }}
                                onChange={(e) => {
                                    setState((prevState) => ({
                                        ...prevState,
                                        [stateKey]: {
                                            ...prevState[stateKey],
                                            remarks: e.target.value
                                        }
                                    }))
                                }}
                            />}
                        </Box>)
                })}
            </Card>
        )
    }, [state])

    const handleEditor = useCallback((stateKey, value) => {
        setState((prevState) => ({
            ...prevState,
            [stateKey]: value || ""
        }))
    }, [])

    const renderBoldItalic = (stateKey) => {
        return (
            <Box marginBottom={"0.5dvw"}>
                <ToolsFieldWithMic
                    Title={formatLabel(stateKey)}
                    EditorRef={state[stateKey] ? state[stateKey] : ''}
                    saveData={handleEditor.bind(this)}
                    isFrom={"nurseAssessment"}
                    minHeight={"12vh"}
                    overallHeight={"auto"}
                    stateName={stateKey}
                />
            </Box>
        )
    }
  

    
    const nurseSelection = () => {
        const { t } = props
        return (
            <Autocomplete
                size='small'
                options={state.NurseList || []}
                getOptionLabel={(option) => option.nurse}
                value={state.nurseName}
                onChange={(event, newValue) => {
                    setState((prevState) => ({
                        ...prevState,
                        nurseName: newValue
                    }))
                }}
                renderInput={(params) => (
                    <TextField
                        sx={{ width: "15dvw" }}
                        {...params}
                        label={t("Select Nurse Name *")}
                    />
                )}
            />
        )
    }

   const onClearHandler = useCallback(() => {
       let nurseId = JSON.parse(localGetItem("NurseId"))
       setState((prevState) => ({
        ...prevState,
        nurseName: (prevState?.NurseList?.length && localGetItem("NurseId")) ? prevState?.NurseList.find((item) => item.staff_id === nurseId) : null,
        ...JSON.parse(JSON.stringify(defaultState)),
        o2_remarks: "",
        notes: "",
    }));
}, [defaultState]);

    const postNurseAssessment = useCallback(() => {
        try {
            if (!state.nurseName?.staff_id) {
                errorMessage("Please Select Nurse Name")
            } else {
                const data = {
                    "clinic_id": state.clinic_id,
                    "ip_admission_id": state.patientData?.id,
                    "nurse_profile_id": state.nurseName?.staff_id || null,
                    "system_wise_assesment": getTrueValueObj(state.system_wise_assesment),
                    "system_wise_assesment_remarks": state?.system_wise_assesment?.remarks || "",
                    "respiratory_system": getTrueArray(state?.respiratory_system),
                    "respiratory_system_remarks": state?.respiratory_system?.remarks || "",
                    "o2_remarks": state.o2_remarks || "",
                    "cardio_vascular_system": getTrueArray(state?.cardio_vascular_system),
                    "cardio_vascular_system_remarks": state?.cardio_vascular_system?.remarks || "",
                    "gastro_intentional_system": getTrueArray(state?.gastro_intentional_system),
                    "gastro_intentional_system_remarks": state?.gastro_intentional_system?.remarks || "",
                    "genito_urinary_system": getTrueArray(state?.genito_urinary_system),
                    "genito_urinary_system_remarks": state?.genito_urinary_system?.remarks || "",
                    "central_nervious_system": getTrueArray(state?.central_nervous_system),
                    "central_nervious_system_remarks": state?.central_nervous_system?.remarks || "",
                    "musculo_skeletal_system": getTrueArray(state?.musculo_skeletal_system),
                    "musculo_skeletal_system_remarks": state?.musculo_skeletal_system?.remarks || "",
                    "emotional_status": getTrueArray(state?.emotional_status),
                    "emotional_status_remarks": state?.emotional_status?.remarks || "",
                    "skin_integrity": getTrueArray(state?.skin_integrity),
                    "skin_integrity_remarks": state?.skin_integrity?.remarks || "",
                    "fall_risk": getTrueArray(state?.falls_risk),
                    "fall_risk_remarks": state?.falls_risk?.remarks || "",
                    "eating_habits": getTrueValueObj(state.eating_habits),
                    "eating_habits_remarks": state?.eating_habits?.remarks || "",
                    "notes": state.notes || "",
                }
                LoaderFunction(true)
                RestAPIService.create(data, Serviceurls.IP_DOCNOTES_NURSE_ASSESSMENTS)
                    .then((response) => {
                        if (response.data.status === "success") {
                            getNurseAssessmentData()
                            LoaderFunction(false)
                            successMessage(response.data.message)
                            let nurseId = JSON.parse(localGetItem("NurseId"))
                            setState((prev) => ({
                                ...prev,
                                nurseName: (prev?.NurseList?.length && localGetItem("NurseId")) ? prev?.NurseList.find((item) => item.staff_id === nurseId) : null,
                            }))
                        }
                    }).catch((error) => {
                        LoaderFunction(false)
                        errorMessage(error?.response?.data?.message || error.message)
                    })
            }
        } catch (error) {
            errorMessage(error.message)
        }
    }, [state.clinic_id, state.patientData?.id, state.nurseName?.staff_id, state.system_wise_assesment])
    
    // render
    const { t } = props
    const attenderDetails = useMemo(() => ({
        lineOne: t("AttenderName"),
        lineTwo: `${state.attender_name ? state.attender_name : " -"}`,
        lineFour: t('ContactNumber'),
        LineFive: `${state.attender_mobile ? state.attender_mobile : "-"}`
    }), [state.attender_name, state.attender_mobile, t]);

    const doctorDetails = useMemo(() => ({
        lineOne: t("ConsultantName"),
        lineTwo: `${state.doctor_name ? state.doctor_name : " -"}`
    }), [state.doctor_name, t]);

    const renderHistory = () => {
        state.historyData?.forEach((element, index) => element.sno = index + 1)
        const columns = [
            {
                field: 'modified_date', headerName: 'Modified Date & Time', headerAlign: 'center', align: 'center', flex: 0.15,
                renderCell: (params) => (
                    <Box><Typography fontSize={'.9vw'}>{params.row.modified_date || '-'}</Typography></Box>
                )
            },
            {
                field: 'nurse_name', headerName: 'Nurse Name', type: 'string', flex: 0.16,
                renderCell: (params) => (
                    <Box component={'div'}>
                        {params.row.nurser_name?.length > 16 ?
                            <Tooltip title={params.row.nurser_name} placement='top'><Typography>{params.row.nurse_name.slice(0, 16) + '...'}</Typography></Tooltip> :
                            <Typography fontSize={'.9vw'}>{params.row.nurse_name || '-'}</Typography>}
                    </Box>
                )
            },
            {
                field: 'field_name', headerName: 'Section', type: 'string', flex: 0.17,
                renderCell: (params) => (
                    <Box component={'div'}>
                        {params.row.field_name?.length > 18 ?
                            <Tooltip title={params.row.field_name} placement='top'><Typography>{params.row.field_name.slice(0, 18) + '...'}</Typography></Tooltip> :
                            <Typography fontSize={'.9vw'}>{params.row.field_name || '-'}</Typography>}
                    </Box>
                )
            },
            {
                field: 'previous_val', headerName: 'Previous Value', type: 'string', flex: 0.18,
                renderCell: (params) => {
                    const input = params.row.previous_val || "";
                    const noTags = input.replace(/<[^>]*>/g, "");
                    const parser = new DOMParser();
                    const decoded = parser.parseFromString(noTags, "text/html").body.textContent || "";
                    return (
                        <Box component={'div'}>
                            {decoded?.length > 18 ?
                                <Tooltip title={decoded} placement='top'><Typography>{decoded.slice(0, 18) + '...'}</Typography></Tooltip> :
                                <Typography fontSize={'.9vw'}>{decoded || '-'}</Typography>}
                        </Box>
                    )
                }
            },
            {
                field: 'updated_val', headerName: 'Updated Value', type: 'string', flex: 0.18,
                renderCell: (params) => {
                    const input = params.row.updated_val || "";
                    const noTags = input.replace(/<[^>]*>/g, "");
                    const parser = new DOMParser();
                    const decoded = parser.parseFromString(noTags, "text/html").body.textContent || "";
                    return (
                        <Box component={'div'}>
                            {decoded?.length > 18 ?
                                <Tooltip title={decoded} placement='top'><Typography>{decoded.slice(0, 18) + '...'}</Typography></Tooltip> :
                                <Typography fontSize={'.9vw'}>{decoded || '-'}</Typography>}
                        </Box>
                    )
                }
            },
            {
                field: 'modified_by', headerName: 'Modified By', type: 'string', flex: 0.16,
                renderCell: (params) => (
                    <Box component={'div'}>
                        {params.row.modified_by?.length > 16 ?
                            <Tooltip title={params.row.modified_by} placement='top'><Typography>{params.row.modified_by.slice(0, 16) + '...'}</Typography></Tooltip> :
                            <Typography fontSize={'.9vw'}>{params.row.modified_by || '-'}</Typography>}
                    </Box>
                )
            },
        ]
        return (
            <Modal
                open={state.isHistory}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '75vw', height: '35.5vw', bgcolor: 'background.paper', boxShadow: 24, borderRadius: '.2vw' }}>
                    <Box display={"flex"} justifyContent={'space-between'} alignItems={'center'} padding={'0.5vw'}>
                        <Typography fontSize={'1.2vw'} fontWeight={'bold'}>{'History'}</Typography>
                        <IconButton onClick={() => { setState((prevState) => ({ ...prevState, isHistory: false })) }}>
                            <img src={ImagePaths.LabCloseButton.default} alt='close' style={{ width: '1vw', height: '1vw' }} />
                        </IconButton>
                    </Box>
                    <hr style={{ color: '#888888' }} />
                    <Box height={'30vw'} margin={'.5vw'}>
                        <DataGrid
                            rows={state.historyData}
                            columns={columns}
                            getRowId={(row) => row['sno']}
                            localeText={{
                                toolbarColumns: "",
                                toolbarDensity: "",
                                toolbarExport: "",
                                toolbarFilters: "",
                                toolbarExportPrint: ""
                            }}
                            components={{
                                NoRowsOverlay: () => (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        {'No Records To Be Shown'}
                                    </Box>
                                )
                            }}
                            headerHeight={40}
                            disableSelectionOnClick
                            pagination={false}
                            hideFooter={true}
                        />
                    </Box>
                </Box>
            </Modal>
        )
    }

    return (
        <Box component={'div'} className='eMed_nur_note_container'>
            <Box display={'flex'} >
                <Box className='eMed_nur_notes_details eMed_patient_nur_notes'>
                    <CommonPatientDetails data={state.patientData} showDetailed={true} />
                </Box>
                <CommonPatientCard details={attenderDetails} showDetailed={true} />
                <ConsultantPatientCard name={doctorDetails} width={props.is_From_IpNotes ? "20dvw" : null} />
                <Tooltip title={"History"} placement='top'>
                    <IconButton onClick={() => { setState((prevState) => ({ ...prevState, isHistory: true })) }}>
                        <img src={ImagePaths.PreHistory.default} alt='history' style={{ width: '2vw', height: '2vw' }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box component={'div'} className='eMed_nur_assessment_content'>
                {renderCard("system_wise_assesment")}
                {renderCard("respiratory_system")}
                {renderBoldItalic("o2_remarks")}
                {renderCard("cardio_vascular_system")}
                {renderCard("gastro_intentional_system")}
                {renderCard("genito_urinary_system")}
                {renderCard("central_nervous_system")}
                {renderCard("musculo_skeletal_system")}
                {renderCard("emotional_status")}
                {renderCard("skin_integrity")}
                {renderCard("falls_risk")}
                {renderCard("eating_habits")}
                {renderBoldItalic("notes")}
            </Box>
            <Box display={"flex"} justifyContent={"flex-end"} alignItems={"center"} gap={"0.625dvw"} height={"5vh"} marginTop={"0.5dvw"} padding={"0.625dvw"}>
                {nurseSelection()}
                <Button variant='outlined' size='small'
                    className='eMed_nur_btn_two'
                    disabled={state.withOutAppointment?.is_ip}
                    onClick={onClearHandler}>{t("Clear")}</Button>
                <Button variant='contained' className='eMed_nur_btn_two' emed_tid="AddCompleted_Btn"
                    sx={{ marginRight: "1dvw" }}
                    size='small'
                    disabled={state.isLoader ? true :
                        props.is_From_IpNotes ? false :
                            CheckAccessFunc("front_office", "In Patients", "Clinical Notes", "Nurse Assessments", "SubTab")?.editAccess ?
                                state.withOutAppointment?.is_ip : true}
                    onClick={postNurseAssessment}>{t("Complete Assessment")}</Button>
            </Box>
            {state.isErrorMsg && <ToastMsg
                severity={'error'}
                msg={state.isErrorMsgText}
                msgPop={msgClose.bind(this)}
            />}
            {state.successMsg && <ToastMsg
                severity={'success'}
                msg={state.successMsgText}
                msgPop={msgClose.bind(this)}
            />}
            <Loader loaderOpen={state.isLoader} />
            {state.isHistory ? renderHistory() : null}
        </Box>
    )
}

export default withTranslation()(NurseAssessments)